import { toast, type ToastOptions } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NotificationsContainer from './NotificationsContainer';
import { AdminRecordModel } from '@feature/adminPanel/types';
import type { SystemIconId } from '@volvo/vcdk-assets';

const toastCommonProps: ToastOptions = {
  position: 'top-center',
  autoClose: 6000,
  pauseOnHover: true,
  hideProgressBar: true,
  icon: false, // icon is handled inside NotificationsContainer
  closeButton: false, // close button is handled inside NotificationsContainer
};

const maxLength = 3400;

const limitToMaxLength = (message: string): string => {
  return message.length > maxLength
    ? message.substring(0, maxLength).concat('...')
    : message;
};

const sendSuccess = (msg: string, timeOut?: number): void => {
  toast.success(
    ({ closeToast, toastProps }) => (
      <NotificationsContainer
        closeToast={closeToast}
        toastProps={toastProps}
        data={{
          message: limitToMaxLength(msg),
          variant: 'success',
        }}
        isPaused={false}
      />
    ),
    {
      ...toastCommonProps,
      autoClose: timeOut ?? toastCommonProps.autoClose,
      className: 'bg-success! min-w-full!',
    }
  );
};

const sendError = (msg: string, timeOut?: number): void => {
  toast.error(
    ({ closeToast, toastProps }) => (
      <NotificationsContainer
        closeToast={closeToast}
        toastProps={toastProps}
        data={{
          message: limitToMaxLength(msg),
          variant: 'error',
        }}
        isPaused={false}
      />
    ),
    {
      ...toastCommonProps,
      autoClose: timeOut ?? toastCommonProps.autoClose,
      className: 'bg-error! min-w-full!',
    }
  );
};

const sendInfo = (msg: string, timeOut?: number): void => {
  toast.info(
    ({ closeToast, toastProps }) => (
      <NotificationsContainer
        closeToast={closeToast}
        toastProps={toastProps}
        data={{
          message: limitToMaxLength(msg),
          variant: 'default',
        }}
        isPaused={false}
      />
    ),
    {
      ...toastCommonProps,
      autoClose: timeOut ?? toastCommonProps.autoClose,
      className: 'bg-info! min-w-full!',
    }
  );
};

const sendWarning = (msg: string, timeOut?: number): void => {
  toast.warning(
    ({ closeToast, toastProps }) => (
      <NotificationsContainer
        closeToast={closeToast}
        toastProps={toastProps}
        data={{
          message: limitToMaxLength(msg),
          variant: 'default',
        }}
        isPaused={false}
      />
    ),
    {
      ...toastCommonProps,
      autoClose: timeOut ?? toastCommonProps.autoClose,
      className: 'bg-warning! min-w-full!',
    }
  );
};

interface WarningAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  iconStart?: SystemIconId;
  onClick?: () => void;
  closeOnClick?: boolean;
}

const sendWarningConfirm = (msg: string, actions: WarningAction[]): void => {
  toast.warning(
    ({ closeToast, toastProps }) => (
      <NotificationsContainer
        closeToast={closeToast}
        toastProps={toastProps}
        data={{
          message: limitToMaxLength(msg),
          variant: 'default',
          centeredMessage: true,
          actions,
        }}
        isPaused={false}
      />
    ),
    {
      ...toastCommonProps,
      autoClose: false,
      closeOnClick: false,
      className: 'bg-warning! min-w-full!',
    }
  );
};

const sendSurvey = (surveyData: AdminRecordModel, timeOut?: number): void => {
  toast.info(
    ({ closeToast, toastProps }) => (
      <NotificationsContainer
        closeToast={closeToast}
        toastProps={toastProps}
        data={{
          message: limitToMaxLength(surveyData.parameterValue),
          formUrl: surveyData.formUrl,
          variant: 'default',
        }}
        isPaused={false}
      />
    ),
    {
      ...toastCommonProps,
      autoClose: timeOut ?? false,
      className: 'bg-info-subtle! min-w-full!',
    }
  );
};

export const NotificationService = {
  sendSuccess,
  sendError,
  sendInfo,
  sendWarning,
  sendWarningConfirm,
  sendSurvey,
};
